import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetCompletedGamesQuery, useGetGameListQuery } from '../services/casino/casinoApi';

// YYYY-MM-DD (native date input & API format)
const dateInput = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CompletedCasino = () => {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState(dateInput(-25));
  const [toDate, setToDate] = useState(dateInput(0));
  const [selectedGame, setSelectedGame] = useState('');
  // Applied filter (changes only on "Get")
  const [filter, setFilter] = useState({ fromDate: dateInput(-25), toDate: dateInput(0), game: '' });

  // Game list for the dropdown
  const { data: gameListData } = useGetGameListQuery();
  const gameList =
    gameListData?.data ?? gameListData?.games ?? (Array.isArray(gameListData) ? gameListData : []);

  // Fetch completed games with date + game filter
  const { data: gamesData, isLoading, isFetching, error } = useGetCompletedGamesQuery(
    { fromDate: filter.fromDate, toDate: filter.toDate, game: filter.game || undefined },
    { pollingInterval: 10000 }
  );

  const games = gamesData?.data || [];
  const totalPL = gamesData?.summary?.totalPL || 0;

  const applyFilter = () => setFilter({ fromDate, toDate, game: selectedGame });

  const handleShowGames = (game, filterDate) => {
    navigate('/casino/casino-details', {
      state: { game, filterDate }
    });
  };

  const handlePL = (gameName, gameKey, filterDate) => {
    // Get userId from localStorage
    const storedUser = localStorage.getItem('user_data');
    let userId = '';
    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        userId = userObj.userId || '';
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
    }
    
    // Use filterDate directly as it's already in YYYY-MM-DD format from API
    const fromDate = filterDate || '';
    const toDate = filterDate || '';
    
    console.log('handlePL - gameName:', gameName, 'gameKey:', gameKey, 'userId:', userId, 'fromDate:', fromDate, 'toDate:', toDate);
    
    // Navigate to company report page
    navigate('/casino/company-report', {
      state: {
        game: gameKey,
        userId: userId,
        gameName: gameName,
        fromDate: fromDate,
        toDate: toDate
      }
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal">
                Completed Casino
              </span>
              <button 
                onClick={handleBack}
                className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 px-5 py-3 border-b border-gray-200">
              <div>
                <label className="block text-xs mb-1 text-gray-500">Game</label>
                <select
                  value={selectedGame}
                  onChange={(e) => setSelectedGame(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm min-w-[180px]"
                >
                  <option value="">All Games</option>
                  {gameList.map((g, i) => {
                    const code = g?.game ?? g?.code ?? g?.key ?? g?.slug ?? g?.gameId ?? g?.id ?? g;
                    const label = g?.name ?? g?.gameName ?? g?.title ?? g?.label ?? code;
                    return (
                      <option key={code ?? i} value={code}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-500">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-500">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <button
                onClick={applyFilter}
                disabled={isFetching}
                className="bg-blue-500 text-white px-5 py-2 rounded hover:bg-blue-600 disabled:opacity-60"
              >
                {isFetching ? 'Loading…' : 'Get'}
              </button>
            </div>

            {/* P/L Display */}
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <div className="text-sm text-gray-600 mb-1">P/L</div>
              <div className={`text-3xl font-semibold ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {totalPL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#3e525f] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Date</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Event Name</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">P/L</th>
                      <th className="py-3 px-4 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {games.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-gray-500 border-t border-gray-200">
                          No data available
                        </td>
                      </tr>
                    ) : (
                      games.map((game, index) => {
                        // Store raw date for API calls
                        const rawDate = game.date; // This should be ISO format from API
                        const displayDate = game.date ? new Date(game.date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }).replace(',', '') : '-';
                        
                        return (
                        <tr key={index} className="hover:bg-gray-50 border-t border-gray-200">
                          <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                            {displayDate}
                          </td>
                          <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{game.eventName}</td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            <span className={`font-semibold ${game.pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {game.pl?.toFixed(2) || '0.00'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleShowGames(game.game, game.filterDate || rawDate)}
                                className="bg-[#3e525f] text-white px-3 py-1.5 text-sm hover:bg-[#2d3f4a] transition-colors"
                              >
                                Show Games
                              </button>
                              <button
                                onClick={() => handlePL(game.eventName, game.game, game.filterDate)}
                                className="bg-[#3e525f] text-white px-3 py-1.5 text-sm hover:bg-[#2d3f4a] transition-colors"
                              >
                                PL
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedCasino;